import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query'
import backend from '@api/backend'
import {
  ISubscriptionDeviceRowRaw,
  ISubscriptionDeviceRow,
  IInvoiceRowRaw,
  IInvoiceRow,
  FinalizeInvoiceData,
  SetSubscriptionOverrideData,
  ISubscriptionOverrideRow,
} from './model/subscriptions.model'
import { getBillingApiError } from '@data/billing/billing'
import {
  notifyApiError,
  notifySuccess,
} from '@common/utils/notify'

// ---------------------------------------------------------------------------
// Transform helpers
// ---------------------------------------------------------------------------

function transformDeviceRow(raw: ISubscriptionDeviceRowRaw): ISubscriptionDeviceRow {
  return {
    deviceId: raw.device_id,
    deviceName: raw.device_name,
    locationId: raw.location_id,
    locationName: raw.location_name,
    billingEntityId: raw.billing_entity_id,
    billingEntityName: raw.billing_entity_name,
    stripeSubscriptionId: raw.stripe_subscription_id,
    stripeRecurringPriceId: raw.stripe_recurring_price_id,
    subscriptionStatus: raw.subscription_status,
    currentPeriodEnd: raw.current_period_end,
    trialEnd: raw.trial_end,
    paymentState: raw.payment_state ?? null,
    paidThrough: raw.paid_through ?? null,
    paidInvoiceId: raw.paid_invoice_id ?? null,
    overrideActive: raw.override_active ?? false,
    overrideReason: raw.override_reason ?? null,
    stripeDerivedStatus: raw.stripe_derived_status ?? null,
    draftInvoiceId: raw.draft_invoice_id ?? null,
    poNumber: raw.po_number ?? null,
    poMode: raw.po_mode ?? 0,
    monitoringPriceMinor: raw.monitoring_price_minor ?? null,
    currency: raw.currency ?? null,
  }
}

function transformInvoiceRow(raw: IInvoiceRowRaw): IInvoiceRow {
  return {
    invoiceId: raw.invoice_id,
    billingEntityId: raw.billing_entity_id,
    subscriptionId: raw.subscription_id,
    number: raw.number,
    amountDue: raw.amount_due,
    amountPaid: raw.amount_paid,
    currency: raw.currency,
    status: raw.status,
    invoicePdf: raw.invoice_pdf,
    hostedInvoiceUrl: raw.hosted_invoice_url,
    periodStart: raw.period_start,
    periodEnd: raw.period_end,
    locationName: raw.location_name,
    deviceName: raw.device_name,
    requiresPo: raw.requires_po,
    poNumber: raw.po_number,
  }
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export const useSubscriptionDevices = (accountId: number | undefined) => {
  return useQuery({
    queryKey: ['subscription-devices', accountId],
    queryFn: () => getSubscriptionDevices(accountId!),
    enabled: !!accountId,
    staleTime: 1000 * 60 * 5, // 5 min — subscription status can change
  })
}

export const useSubscriptionDevicesMulti = (accountIds: number[]) => {
  const results = useQueries({
    queries: accountIds.map((id) => ({
      queryKey: ['subscription-devices', id],
      queryFn: () => getSubscriptionDevices(id),
      staleTime: 1000 * 60 * 5,
    })),
  })
  return {
    data: results.flatMap((r) => r.data ?? []),
    isLoading: results.some((r) => r.isLoading),
    isError: results.some((r) => r.isError),
  }
}

export const useInvoiceHistory = (accountId: number | undefined) => {
  return useQuery({
    queryKey: ['invoice-history', accountId],
    queryFn: () => getInvoiceHistory(accountId!),
    enabled: !!accountId,
    staleTime: 1000 * 60 * 15,
  })
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/**
 * Attaches a PO number to a draft invoice (when supplied) and finalizes it,
 * so Stripe sends it to the customer. The backend flow is identical either
 * way — the PO is simply omitted for entities that don't require one.
 */
export const useFinalizeInvoice = (accountId: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: FinalizeInvoiceData) => finalizeInvoice(accountId, data),
    onSuccess: (updated: IInvoiceRow) => {
      queryClient.setQueryData(
        ['invoice-history', accountId],
        (old: IInvoiceRow[] | undefined) =>
          (old ?? []).map((inv) => (inv.invoiceId === updated.invoiceId ? updated : inv)),
      )
      // Finalizing clears the device's held draft and can change its badge,
      // so the device rows must be refetched — previously they stayed stale
      // for up to the 5-minute staleTime.
      queryClient.invalidateQueries({ queryKey: ['subscription-devices', accountId] })
      notifySuccess('Invoice finalized and sent')
    },
    onError: (error) => {
      // Field-scoped errors (e.g. a missing PO number) are rendered inline in
      // the modal — don't also raise a toast for them.
      if (getBillingApiError(error)?.field) return
      notifyApiError(error, 'Failed to finalize invoice')
    },
  })
}

/**
 * Sets a temporary SMART FLOW status for a device (SF-APP-009).
 *
 * Used during the Xero-to-Stripe migration, where Stripe reports active/paid
 * but the legacy renewal is actually overdue. This never changes the Stripe
 * subscription — it only changes what SMART FLOW displays.
 */
export const useSetSubscriptionOverride = (accountId: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SetSubscriptionOverrideData) => setSubscriptionOverride(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-devices', accountId] })
      queryClient.invalidateQueries({ queryKey: ['customer-renewal-detail'] })
      queryClient.invalidateQueries({ queryKey: ['renewals-list'] })
      queryClient.invalidateQueries({ queryKey: ['renewal-summary'] })
      notifySuccess('Status override applied')
    },
    onError: (error) => {
      if (getBillingApiError(error)?.field) return
      notifyApiError(error, 'Failed to set status override')
    },
  })
}

/** Clears an override so the device reverts to its Stripe-derived status. */
export const useClearSubscriptionOverride = (accountId: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (deviceAccountLinkId: number) => clearSubscriptionOverride(deviceAccountLinkId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-devices', accountId] })
      queryClient.invalidateQueries({ queryKey: ['customer-renewal-detail'] })
      queryClient.invalidateQueries({ queryKey: ['renewals-list'] })
      queryClient.invalidateQueries({ queryKey: ['renewal-summary'] })
      notifySuccess('Status override cleared')
    },
    onError: (error) => {
      notifyApiError(error, 'Failed to clear status override')
    },
  })
}

// ---------------------------------------------------------------------------
// Async functions
// ---------------------------------------------------------------------------

async function setSubscriptionOverride(
  data: SetSubscriptionOverrideData,
): Promise<ISubscriptionOverrideRow> {
  const response = await backend.post('/subscriptions/status_override', data)
  return response.data as ISubscriptionOverrideRow
}

async function clearSubscriptionOverride(
  deviceAccountLinkId: number,
): Promise<ISubscriptionOverrideRow> {
  const response = await backend.delete(
    `/subscriptions/status_override/${deviceAccountLinkId}`,
  )
  return response.data as ISubscriptionOverrideRow
}

async function getSubscriptionDevices(accountId: number): Promise<ISubscriptionDeviceRow[]> {
  const response = await backend.get('/subscriptions/devices', {
    params: { account_id: accountId },
  })
  return (response.data as ISubscriptionDeviceRowRaw[]).map(transformDeviceRow)
}

async function getInvoiceHistory(accountId: number): Promise<IInvoiceRow[]> {
  const response = await backend.get('/subscriptions/invoices', {
    params: { account_id: accountId },
  })
  return (response.data as IInvoiceRowRaw[]).map(transformInvoiceRow)
}

async function finalizeInvoice(
  accountId: number,
  data: FinalizeInvoiceData,
): Promise<IInvoiceRow> {
  const response = await backend.post('/subscriptions/invoices/finalize', data, {
    params: { account_id: accountId },
  })
  return transformInvoiceRow(response.data as IInvoiceRowRaw)
}
